import { useState } from 'react';
import {
    Box, Button, Grid, IconButton, MenuItem, Paper, TextField, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress,
} from '@mui/material';
import { Check as CheckIcon, Refresh as RefreshIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';

export default function PurchasingDashboard() {
    const { error: showError } = useNotify();
    const formatYmd = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    const ALL = '__ALL__';
    const today = new Date();
    const minus14 = new Date(today);
    minus14.setDate(today.getDate() - 14);
    const mpOptions = [
        { value: 1, label: 'Wildberries' },
        { value: 2, label: 'Ozon' },
        { value: 3, label: 'Yandex' },
    ];

    const [dateFrom, setDateFrom] = useState(formatYmd(minus14));
    const [dateTo, setDateTo] = useState(formatYmd(today));
    const [filters, setFilters] = useState({});
    const [mpSel, setMpSel] = useState([]);
    const [article, setArticle] = useState('');
    const [loading, setLoading] = useState(false);
    const [byDate, setByDate] = useState([]);
    const [byProduct, setByProduct] = useState([]);
    const [byColor, setByColor] = useState([]);
    const [selDate, setSelDate] = useState([]);
    const [selPodgruppa, setSelPodgruppa] = useState([]);
    const [selArticle, setSelArticle] = useState([]);

    const cellStyle = { py: 0.5, px: 1, fontSize: '0.85rem', whiteSpace: 'nowrap' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };
    const fmtNum = (v) => {
        if (v == null || v === '') return '0';
        const n = Number(v);
        if (isNaN(n)) return '0';
        return n.toLocaleString('ru-RU');
    };
    const buildParams = (overrides = {}) => {
        const sd = overrides.selDate !== undefined ? overrides.selDate : selDate;
        const sp = overrides.selPodgruppa !== undefined ? overrides.selPodgruppa : selPodgruppa;
        const sa = overrides.selArticle !== undefined ? overrides.selArticle : selArticle;
        const filterParams = buildFilterParams(filters);
        delete filterParams.mp;
        delete filterParams.article;
        const params = { ...filterParams };
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (mpSel.length) params.mp = mpSel.join(',');
        if (article.trim()) params.article = article.trim();
        if (sd.length) params.sel_date = sd.join(',');
        if (sp.length) params.sel_podgruppa = sp.join(',');
        if (sa.length) params.sel_article = sa.join(',');
        return params;
    };

    const fetchData = async (params) => {
        setLoading(true);
        try {
            const resp = await axios.get('/analytics/purchasing/dashboard', { params });
            setByDate(resp.data.byDate || []);
            setByProduct(resp.data.byProduct || []);
            setByColor(resp.data.byColor || []);
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Error');
        } finally { setLoading(false); }
    };

    const load = () => {
        setSelDate([]); setSelPodgruppa([]); setSelArticle([]);
        fetchData(buildParams({ selDate: [], selPodgruppa: [], selArticle: [] }));
    };

    const toggleSelection = (prev, val, multi) => {
        if (multi) {
            return prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val];
        }
        return prev.length === 1 && prev[0] === val ? [] : [val];
    };
    const onSelectDate = (dt, e) => {
        const next = toggleSelection(selDate, dt, e.ctrlKey || e.metaKey);
        setSelDate(next);
        fetchData(buildParams({ selDate: next }));
    };
    const onSelectPodgruppa = (pg, e) => {
        const next = toggleSelection(selPodgruppa, pg, e.ctrlKey || e.metaKey);
        setSelPodgruppa(next);
        fetchData(buildParams({ selPodgruppa: next }));
    };
    const onSelectArticle = (art, e) => {
        const next = toggleSelection(selArticle, art, e.ctrlKey || e.metaKey);
        setSelArticle(next);
        fetchData(buildParams({ selArticle: next }));
    };

    const exportToExcel = (rows, colKey, colLabel, title) => {
        const data = rows.map(r => ({ [colLabel]: r[colKey], 'Заказы': r.ordersCount, 'Штуки': r.unitsCount }));
        data.push({
            [colLabel]: 'Итого',
            'Заказы': rows.reduce((s, r) => s + r.ordersCount, 0),
            'Штуки': rows.reduce((s, r) => s + Number(r.unitsCount || 0), 0),
        });
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
        XLSX.writeFile(wb, `${title}.xlsx`);
    };

    const SimpleTable = ({ title, rows, colKey, colLabel, selectedValues, onSelect }) => (
        <Paper sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
                <IconButton size="small" onClick={() => exportToExcel(rows, colKey, colLabel, title)}
                    disabled={!rows.length} title="Скачать в Excel">
                    <FileDownloadIcon fontSize="small" />
                </IconButton>
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerStyle}>{colLabel}</TableCell>
                            <TableCell sx={headerStyle} align="right">Заказы</TableCell>
                            <TableCell sx={headerStyle} align="right">Штуки</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => {
                            const val = row[colKey];
                            const isSelected = selectedValues.includes(val);
                            return (
                                <TableRow key={val ?? idx} hover
                                    onClick={(e) => onSelect(val, e)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: isSelected ? 'primary.light' : 'inherit',
                                        '&:hover': { bgcolor: isSelected ? 'primary.light' : undefined },
                                    }}>
                                    <TableCell sx={{ ...cellStyle, fontWeight: isSelected ? 'bold' : 'normal', color: isSelected ? 'primary.contrastText' : 'inherit' }}>{val}</TableCell>
                                    <TableCell sx={{ ...cellStyle, fontWeight: isSelected ? 'bold' : 'normal', color: isSelected ? 'primary.contrastText' : 'inherit' }} align="right">{fmtNum(row.ordersCount)}</TableCell>
                                    <TableCell sx={{ ...cellStyle, fontWeight: isSelected ? 'bold' : 'normal', color: isSelected ? 'primary.contrastText' : 'inherit' }} align="right">{fmtNum(row.unitsCount)}</TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length > 0 && (
                            <TableRow>
                                <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}>Итого</TableCell>
                                <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }} align="right">
                                    {fmtNum(rows.reduce((s, r) => s + r.ordersCount, 0))}
                                </TableCell>
                                <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }} align="right">
                                    {fmtNum(rows.reduce((s, r) => s + Number(r.unitsCount || 0), 0))}
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={3} sx={cellStyle} align="center">Нет данных</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    const renderMpSelect = () => (
        <TextField select label="МП" value={mpSel}
            onChange={e => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value;
                if (Array.isArray(val) && val.includes(ALL)) { setMpSel([]); return; }
                setMpSel(val);
            }}
            size="small" fullWidth
            SelectProps={{
                multiple: true,
                renderValue: (sel) => sel.length === 0
                    ? 'Все'
                    : sel.map(v => mpOptions.find(o => o.value === v)?.label || v).join(', '),
            }}>
            <MenuItem value={ALL}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <em>Все</em>{mpSel.length === 0 && <CheckIcon fontSize="small" />}
                </Box>
            </MenuItem>
            {mpOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{o.label}</span>{mpSel.includes(o.value) && <CheckIcon fontSize="small" />}
                    </Box>
                </MenuItem>
            ))}
        </TextField>
    );

    return (
        <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Дашборд закупки</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Штуки = заказы × кратность из «Товары МП». Пустая кратность считается как 1.
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2.5}>
                        <DateRangeField valueFrom={dateFrom} valueTo={dateTo}
                            onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={1}>{renderMpSelect()}</Grid>
                    <Grid item xs={6} sm={4} md={1.5}>
                        <TextField label="Артикул" value={article}
                            onChange={e => setArticle(e.target.value)}
                            size="small" fullWidth placeholder="Поиск..." />
                    </Grid>
                    <Grid item xs={12} sm={4} md={1.5}>
                        <Button variant="contained" onClick={load} disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}>
                            Загрузить
                        </Button>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <ProductFilters value={filters} onChange={setFilters} showMp={false} showArticle={false} />
                </Box>
            </Paper>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <SimpleTable title="По датам" rows={byDate} colKey="dt" colLabel="Дата" selectedValues={selDate} onSelect={onSelectDate} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <SimpleTable title="По подгруппам" rows={byProduct} colKey="podgruppa" colLabel="Подгруппа" selectedValues={selPodgruppa} onSelect={onSelectPodgruppa} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <SimpleTable title="По товарам" rows={byColor} colKey="article_number" colLabel="Артикул" selectedValues={selArticle} onSelect={onSelectArticle} />
                </Grid>
            </Grid>
        </Box>
    );
}
